import type { StatusColorName, StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiFileDocumentOutline,
  mdiInboxArrowDownOutline,
  mdiMessageTextOutline,
  mdiPhoneInTalkOutline,
} from "@mdi/js"
import type {
  CaseCommunicationLetterDelivery,
  CaseCommunicationTimelineItem,
  KehrblattCaseCommunicationProviderProgress,
} from "../../../../../../../../api/kehrblatt/case-communications"
import type {
  ContactOrder,
  ContactOrderStep,
} from "../../../../../../../../api/kehrblatt/contact-orders"
import { resolvePlannedContactPoint } from "../../../_logic/planned-contact-point"
import {
  buildCommunicationAppointmentContextTags,
  buildWorkOrderPropertyContextTag,
  buildWorkOrderSummaryContextTag,
  type WorkOrderAppointmentTagPresentation,
  type WorkOrderPropertyContextTagPresentation,
} from "../../../_logic/context-tags"
import type { WorkOrderSummaryTagPresentation } from "../../../../../../_shared/_logic/summary-tag"
import {
  buildContactStepExecutionStatusDefinition,
  resolveContactStepExecutionStatusInfo,
} from "../../../_logic/contact-step-execution-status"
import {
  communicationChannelLabel,
  communicationExecutionStatus,
  communicationMessageResult,
  communicationMessageType,
} from "../../../_logic/case-communications-presentation"
import type {
  CommunicationPlanRowsInput,
  ContactOrderPlanHint,
  ContactOrderPlanRow,
  ContactOrderPlanRowsInput,
  StepActionContext,
  WorkOrderPlanningRowsInput,
} from "../../../_logic/communication-plan-table-contract"
import { buildCommunicationContactPresentation } from "./contactPresentation"
import { buildLastCommunicationSummary } from "./communicationSummary"
import { buildContactStepPresentation } from "./contactStepPresentation"
import { buildHistoricalLetterDeliveryPresentation } from "./letterDeliveryPresentation"
import {
  buildRecipientTagPresentation,
  isUsableContactPointTarget,
  normalizeString,
} from "./recipientPresentation"

type CaseCommunicationProjection = {
  id: string
  organization_id: string
  caseCommunication: CaseCommunicationTimelineItem
  letter_delivery?: CaseCommunicationLetterDelivery | null
  work_order_id: string
  schedule_event_id?: string | null
  contact_order_id?: string | null
  contact_step_id?: string | null
  origin_kind: string
  purpose: string
  channel: string
  direction: string
  status: string
  lifecycle_status?: string | null
  delivery_status?: string | null
  business_outcome?: string | null
  recipient_value?: string | null
  recipient_display_name?: string | null
  recipient_role?: string | null
  recipient_kind?: string | null
  recipient_is_verified?: boolean | null
  recipient_lifecycle_status?: string | null
  recipient_is_usable?: boolean | null
  recipient_source?: string | null
  recipient_confidence?: string | null
  subject?: string | null
  template_key?: string | null
  template_label?: string | null
  template_purpose?: string | null
  template_version?: string | null
  provider?: string | null
  provider_ref?: string | null
  provider_status?: string | null
  provider_progress?: KehrblattCaseCommunicationProviderProgress | null
  failure_reason?: string | null
  skipped_reason?: string | null
  suppression_reason?: string | null
  appointment_refs?: CaseCommunicationTimelineItem["appointment_refs"]
  queued_at?: string | null
  sent_at?: string | null
  failed_at?: string | null
  created_at: string
  updated_at: string
}

export const formatContactOrderPlanDateTime = (value: string | null | undefined) => {
  if (!value) return "–"

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return "–"

  return `${parsed.toLocaleDateString("de-DE")} ${parsed.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

const normalizeCode = (value?: string | null) =>
  String(value || "")
    .trim()
    .toUpperCase()

const statusColorNames = new Set<StatusColorName>([
  "emerald",
  "teal",
  "cyan",
  "blue",
  "indigo",
  "violet",
  "magenta",
  "rose",
  "orange",
  "amber",
])

const hintColor = (value: StatusDefinition["color"]): StatusColorName => {
  return statusColorNames.has(value as StatusColorName) ? (value as StatusColorName) : "blue"
}

const formatSublineDateTime = (value: string | null | undefined) => {
  const formatted = formatContactOrderPlanDateTime(value)

  return formatted === "–" ? null : formatted
}

const timestampFromValues = (values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (!value) continue

    const parsed = new Date(value).getTime()

    if (!Number.isNaN(parsed)) return parsed
  }

  return 0
}

const channelLabel = (channel?: string | null) => {
  const normalized = normalizeCode(channel)

  if (normalized === "SMS") return "SMS"

  if (normalized === "WHATSAPP") return "WhatsApp"

  if (normalized === "VOICE_BOT") return "Telefonbot"

  const label = communicationChannelLabel(channel) ?? "Kontaktweg"

  return normalized === "MOBILE_MESSAGE" ? "mobile Nachricht" : label
}

const contactPerChannelLabel = (channel?: string | null) => {
  const label = channelLabel(channel)

  return label === "Kontaktweg" ? "Kontaktweg" : `Kontakt per ${label}`
}

const UNKNOWN_TEMPLATE_LABEL = "nicht hinterlegt"

const normalizeTemplateKey = (value: string | null | undefined) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

const templateLabelForChannel = (channel?: string | null) => {
  const normalized = normalizeCode(channel)

  if (normalized === "EMAIL") return "E-Mail zur Terminankündigung"

  if (["MOBILE_MESSAGE", "SMS", "RCS", "WHATSAPP"].includes(normalized))
    return "Mobile Terminankündigung"

  if (["PHONE", "VOICE", "VOICE_BOT"].includes(normalized)) return "Telefonvorlage Outbound"

  if (normalized === "LETTER") return "Terminbrief"

  return null
}

const templateLabelForKey = (key: string | null | undefined, channel?: string | null) => {
  const normalizedKey = normalizeTemplateKey(key)
  const normalizedChannel = normalizeCode(channel)
  const emailLabels: Record<string, string> = {
    announcement: "E-Mail zur Terminankündigung",
    email: "E-Mail zur Terminankündigung",
    appointment_announcement: "E-Mail zur Terminankündigung",
    confirmation: "E-Mail zur Terminbestätigung",
    appointment_confirmation: "E-Mail zur Terminbestätigung",
    cancellation: "E-Mail bei Stornierung",
    appointment_cancellation: "E-Mail bei Stornierung",
    cancel_replan_later: "E-Mail bei Stornierung",
    appointment_cancel_replan_later: "E-Mail bei Stornierung",
    reschedule: "E-Mail bei Verschiebung",
    appointment_reschedule: "E-Mail bei Verschiebung",
    follow_up: "E-Mail-Follow-up",
    email_follow_up: "E-Mail-Follow-up",
    reminder: "E-Mail-Follow-up",
  }
  const mobileLabels: Record<string, string> = {
    announcement: "Mobile Terminankündigung",
    mobile_message: "Mobile Terminankündigung",
    sms: "Mobile Terminankündigung",
    appointment_announcement: "Mobile Terminankündigung",
    reschedule: "Mobile Terminverschiebung",
    appointment_reschedule: "Mobile Terminverschiebung",
  }
  const phoneLabels: Record<string, string> = {
    voice: "Telefonvorlage Outbound",
    phone: "Telefonvorlage Outbound",
    outbound: "Telefonvorlage Outbound",
    inbound: "Telefonvorlage Inbound",
  }
  const letterLabels: Record<string, string> = {
    letter: "Terminbrief",
    appointment_letter: "Terminbrief",
    appointment_announcement: "Terminbrief",
    personal: "Persönlicher Terminbrief",
    personal_letter: "Persönlicher Terminbrief",
    impersonal: "Unpersönlicher Terminbrief",
    impersonal_letter: "Unpersönlicher Terminbrief",
    klebezettel: "Klebezettel",
    generic_klebezettel: "Klebezettel",
  }

  if (normalizedChannel === "EMAIL") return emailLabels[normalizedKey] ?? null

  if (["MOBILE_MESSAGE", "SMS", "RCS", "WHATSAPP"].includes(normalizedChannel)) {
    return mobileLabels[normalizedKey] ?? null
  }

  if (["PHONE", "VOICE", "VOICE_BOT"].includes(normalizedChannel))
    return phoneLabels[normalizedKey] ?? null

  if (normalizedChannel === "LETTER") return letterLabels[normalizedKey] ?? null

  return (
    emailLabels[normalizedKey] ??
    mobileLabels[normalizedKey] ??
    phoneLabels[normalizedKey] ??
    letterLabels[normalizedKey] ??
    null
  )
}

const templateLabelForPurpose = (purpose: string | null | undefined, channel?: string | null) => {
  const purposeKey: Record<string, string> = {
    APPOINTMENT_ANNOUNCEMENT: "announcement",
    APPOINTMENT_CONFIRMATION: "confirmation",
    APPOINTMENT_CANCELLATION: "cancellation",
    APPOINTMENT_CANCEL_REPLAN_LATER: "cancellation",
    APPOINTMENT_RESCHEDULE: "reschedule",
    APPOINTMENT_REMINDER: "follow_up",
    LETTER_DELIVERY: "letter",
    BOOKED: "confirmation",
    RESCHEDULED: "reschedule",
    CANCEL_REPLAN_LATER: "cancellation",
  }
  const key = purposeKey[normalizeCode(purpose)]

  return key ? templateLabelForKey(key, channel) : null
}

const normalizeTemplateLabel = (value: string | null | undefined, channel?: string | null) => {
  const text = normalizeString(value)

  if (!text) return null

  const withoutPrefix = text.replace(/^Vorlage:\s*/i, "").trim()
  const genericMatch = withoutPrefix.match(/^Kontakt per\s+(.+)$/i)

  if (genericMatch?.[1]) {
    const rawChannel = genericMatch[1]
    const normalizedGeneric = normalizeCode(rawChannel)

    return templateLabelForChannel(normalizedGeneric) || templateLabelForChannel(channel)
  }

  const normalized = normalizeCode(withoutPrefix)

  if (
    [
      "LETTER",
      "MOBILE_MESSAGE",
      "SMS",
      "RCS",
      "WHATSAPP",
      "EMAIL",
      "PHONE",
      "VOICE",
      "VOICE_BOT",
    ].includes(normalized)
  ) {
    return templateLabelForChannel(normalized)
  }

  return templateLabelForKey(withoutPrefix, channel) || withoutPrefix
}

const resolveTemplateLabel = ({
  label,
  key,
  purpose,
  channel,
}: {
  label?: string | null
  key?: string | null
  purpose?: string | null
  channel?: string | null
}) =>
  normalizeTemplateLabel(label, channel) ||
  templateLabelForKey(key, channel) ||
  templateLabelForPurpose(purpose, channel) ||
  templateLabelForChannel(channel) ||
  UNKNOWN_TEMPLATE_LABEL

const messageDefinitionForChannel = ({
  value,
  channel,
  source,
}: {
  value?: string | null
  channel?: string | null
  source: "planned" | "communication" | "case_communication"
}): StatusDefinition => {
  const normalizedChannel = normalizeCode(channel)
  const normalizedValue = normalizeCode(value) || `${source}:${normalizedChannel || "UNKNOWN"}`
  const resolvedLabel = contactPerChannelLabel(channel)

  if (source === "case_communication" && normalizedChannel === "INBOUND") {
    return {
      value: normalizedValue,
      label: resolvedLabel,
      icon: mdiInboxArrowDownOutline,
      color: "teal",
    }
  }

  if (normalizedChannel === "LETTER") {
    return {
      value: normalizedValue,
      label: resolvedLabel,
      icon: mdiFileDocumentOutline,
      color: "orange",
    }
  }

  if (normalizedChannel === "PHONE" || normalizedChannel === "VOICE_BOT") {
    return {
      value: normalizedValue,
      label: resolvedLabel,
      icon: mdiPhoneInTalkOutline,
      color: "amber",
    }
  }

  return {
    value: normalizedValue,
    label: resolvedLabel,
    icon: mdiMessageTextOutline,
    color: "blue",
  }
}

const isManualLetterDeliveryStep = (step: ContactOrderStep) => {
  return (
    normalizeCode(step.channel) === "LETTER" && step.execution_mode === "manual_letter_delivery"
  )
}

const manualLetterDueDateForStep = (step: ContactOrderStep) =>
  normalizeString(step.next_action_at) || normalizeString(step.due_at)

const statusSubLabelForStep = (step: ContactOrderStep, now: number) => {
  const status = normalizeCode(step.status)

  if (status === "WAITING") {
    const waitUntil = formatSublineDateTime(step.response_deadline_at)

    if (waitUntil) return `Wartet bis ${waitUntil}`
  }

  if (isManualLetterDeliveryStep(step)) {
    const letterDueAt = manualLetterDueDateForStep(step)
    const formattedLetterDueAt = formatSublineDateTime(letterDueAt)

    if (formattedLetterDueAt) {
      const timestamp = new Date(letterDueAt as string).getTime()
      const prefix =
        !Number.isNaN(timestamp) && timestamp <= now ? "Zustellbar seit" : "Zustellbar ab"

      return `${prefix} ${formattedLetterDueAt}`
    }
  }

  const nextAttempt = formatSublineDateTime(step.next_action_at)

  if (nextAttempt) return `Nächster Versuch ${nextAttempt}`

  const dueAt = formatSublineDateTime(step.due_at)

  return dueAt ? `Geplant für ${dueAt}` : null
}

const communicationTimestamp = (communication: CaseCommunicationProjection) =>
  timestampFromValues([
    communication.sent_at,
    communication.failed_at,
    communication.queued_at,
    communication.created_at,
  ])

const communicationStatusSubLabel = (communication: CaseCommunicationProjection) => {
  const status = normalizeCode(communication.status)
  const direction = normalizeCode(communication.direction)

  if (direction === "INBOUND") {
    const value = formatSublineDateTime(communication.sent_at || communication.created_at)

    return value ? `Eingegangen am ${value}` : null
  }

  if (status === "FAILED") {
    const value = formatSublineDateTime(communication.failed_at || communication.updated_at)

    return value ? `Fehlgeschlagen am ${value}` : null
  }

  if (status === "SENT") {
    const value = formatSublineDateTime(communication.sent_at || communication.updated_at)

    return value ? `Gesendet am ${value}` : null
  }

  if (status === "PLANNED" || status === "QUEUED") {
    const value = formatSublineDateTime(communication.queued_at || communication.created_at)

    return value ? `Geplant für ${value}` : null
  }

  const value = formatSublineDateTime(communication.updated_at || communication.created_at)

  return value ? `Dokumentiert am ${value}` : null
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

const deliveryProgressTooltipHtml = (...values: Array<string | null | undefined>) =>
  values
    .filter(Boolean)
    .map((value) => escapeHtml(String(value)))
    .join("<br>")

const metaObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const firstNormalizedString = (...values: unknown[]) => {
  for (const value of values) {
    const text = normalizeString(value)

    if (text) return text
  }

  return null
}

const briefdienstDetail = (value: string | null | undefined) =>
  value?.replace(/\b(?:OnlineBrief24|Onlinebrief24|OB24)\b/g, "Der Briefdienst") ?? null

const providerProgressForCommunication = (
  communication: CaseCommunicationProjection | null | undefined,
) => {
  if (!communication) return null

  const providerProgress = communication.provider_progress ?? {}
  const status = normalizeCode(communication.status)
  const lifecycleStatus = normalizeCode(communication.lifecycle_status)
  const deliveryStatus = normalizeCode(communication.delivery_status)
  const provider = String(communication.provider || "").toLowerCase()

  if (!provider) {
    const isDeliveredLetter =
      normalizeCode(communication.channel) === "LETTER" &&
      (status === "SENT" ||
        status === "DELIVERED" ||
        status === "SUCCEEDED" ||
        lifecycleStatus === "COMPLETED" ||
        lifecycleStatus === "SUCCEEDED" ||
        deliveryStatus === "SENT" ||
        deliveryStatus === "DELIVERED")

    if (!isDeliveredLetter) return null

    const tooltipHtml = "Der Brief wurde händisch zugestellt."

    return {
      label: "Händisch zugestellt",
      subLabel: "ohne Briefdienst",
      detail: tooltipHtml,
      tooltipHtml,
      color: "emerald" satisfies StatusColorName,
    }
  }

  const progressStatus = String(providerProgress.status || "").toLowerCase()
  const progressDetail =
    briefdienstDetail(
      normalizeString(providerProgress.detail) || communication.failure_reason || null,
    ) || null
  const providerRef = normalizeString(communication.provider_ref)
  const providerStatus =
    normalizeString(providerProgress.provider_status) ||
    normalizeString(communication.provider_status)
  const dispatchDate = normalizeString(providerProgress.dispatch_date)
  const trackingCode = normalizeString(providerProgress.tracking_code)
  const referenceLabel = providerRef ? `Ref. ${providerRef}` : null
  const tooltip = deliveryProgressTooltipHtml(
    progressDetail,
    dispatchDate ? `Versanddatum: ${dispatchDate}` : null,
    trackingCode ? `Sendungsverfolgung: ${trackingCode}` : null,
    providerRef ? `Briefdienst-Referenz: ${providerRef}` : null,
  )
  const buildProgress = (label: string, color: StatusColorName, fallbackDetail: string) => ({
    label,
    subLabel: referenceLabel,
    detail: progressDetail || fallbackDetail,
    tooltipHtml: tooltip || fallbackDetail,
    color,
  })

  if (provider !== "onlinebrief24") {
    if (progressStatus === "delivered" || progressStatus === "sent") {
      return buildProgress(
        "Gesendet",
        "emerald",
        progressDetail || "Die Kommunikation wurde gesendet.",
      )
    }

    if (progressStatus === "read") {
      return buildProgress(
        "Gelesen",
        "emerald",
        progressDetail || "Die Kommunikation wurde gelesen.",
      )
    }

    if (progressStatus === "answered") {
      return buildProgress("Geantwortet", "emerald", progressDetail || "Es liegt eine Antwort vor.")
    }

    if (progressStatus === "accepted" || progressStatus === "processing") {
      return buildProgress(
        "an Anbieter übergeben",
        "blue",
        progressDetail || "Der Anbieter verarbeitet die Nachricht.",
      )
    }

    if (progressStatus === "no_answer") {
      return buildProgress(
        "Nicht erreicht",
        "amber",
        progressDetail || "Der Kontakt wurde nicht erreicht.",
      )
    }

    if (progressStatus === "failed") {
      return buildProgress(
        "Fehler",
        "orange",
        progressDetail || "Die Kommunikation ist fehlgeschlagen.",
      )
    }

    if (progressStatus === "retrying") {
      return buildProgress(
        "Wird erneut geprüft",
        "amber",
        progressDetail || "Der Status wird erneut geprüft.",
      )
    }

    return null
  }

  const rawBriefdienstStatus = String(providerStatus || progressStatus || "").toLowerCase()
  const buildBriefdienstProgress = (
    label: string,
    subLabel: string,
    color: StatusColorName,
    fallbackDetail: string,
  ) => ({
    label,
    subLabel,
    detail: fallbackDetail,
    tooltipHtml: tooltip || fallbackDetail,
    color,
  })

  if (rawBriefdienstStatus === "queue") {
    return buildBriefdienstProgress(
      "Brief wartet auf Versand",
      "beim Briefdienst eingegangen",
      "blue",
      "Der Briefauftrag wartet beim Briefdienst auf Verarbeitung.",
    )
  }

  if (rawBriefdienstStatus === "hold") {
    return buildBriefdienstProgress(
      "Brief angehalten",
      "Versand pausiert",
      "amber",
      "Der Briefauftrag ist beim Briefdienst angehalten.",
    )
  }

  if (rawBriefdienstStatus === "draft") {
    return buildBriefdienstProgress(
      "Brief noch nicht versendet",
      "beim Briefdienst noch im Entwurf",
      "amber",
      "Der Briefauftrag ist beim Briefdienst noch nicht versendet.",
    )
  }

  if (
    rawBriefdienstStatus === "done" ||
    rawBriefdienstStatus === "tracking_available" ||
    rawBriefdienstStatus === "sent"
  ) {
    return buildBriefdienstProgress(
      "Brief versendet",
      "vom Briefdienst verarbeitet",
      "emerald",
      "Der Briefauftrag wurde vom Briefdienst verarbeitet.",
    )
  }

  if (rawBriefdienstStatus === "canceled") {
    return buildBriefdienstProgress(
      "Briefauftrag storniert",
      "wird nicht verschickt",
      "orange",
      "Der Briefauftrag wurde beim Briefdienst storniert.",
    )
  }

  if (rawBriefdienstStatus === "retrying") {
    return buildBriefdienstProgress(
      "Status wird geprüft",
      "Briefdienst antwortet noch nicht",
      "amber",
      "Der Briefdienst-Status konnte nicht sicher gelesen werden.",
    )
  }

  if (
    rawBriefdienstStatus === "processing" ||
    rawBriefdienstStatus === "accepted" ||
    rawBriefdienstStatus === "queued"
  ) {
    return buildBriefdienstProgress(
      "Brief wartet auf Versand",
      "beim Briefdienst eingegangen",
      "blue",
      "Der Briefauftrag wartet beim Briefdienst auf Verarbeitung.",
    )
  }

  if (rawBriefdienstStatus === "held") {
    return buildBriefdienstProgress(
      "Brief angehalten",
      "Versand pausiert",
      "amber",
      "Der Briefauftrag ist beim Briefdienst angehalten.",
    )
  }

  if (rawBriefdienstStatus === "failed") {
    return buildBriefdienstProgress(
      "Fehler beim Briefauftrag",
      "Briefdienst konnte nicht verarbeiten",
      "orange",
      "Der Briefdienst meldet einen Fehler beim Briefauftrag.",
    )
  }

  if (progressStatus === "accepted" || deliveryStatus === "ACCEPTED" || status === "QUEUED") {
    return {
      label: "Brief wartet auf Versand",
      subLabel: "beim Briefdienst eingegangen",
      detail: "Der Briefauftrag wartet beim Briefdienst auf Verarbeitung.",
      color: "blue" as StatusDefinition["color"],
      tooltipHtml: tooltip || "Der Briefauftrag wartet beim Briefdienst auf Verarbeitung.",
    }
  }

  if (status === "FAILED" || lifecycleStatus === "FAILED" || deliveryStatus === "FAILED") {
    return {
      label: "Fehler beim Briefauftrag",
      subLabel: "Briefdienst konnte nicht verarbeiten",
      detail: "Der Briefdienst konnte den Briefauftrag nicht annehmen.",
      color: "orange" as StatusDefinition["color"],
      tooltipHtml: tooltip || "Der Briefdienst konnte den Briefauftrag nicht annehmen.",
    }
  }

  return null
}

const applyDeliveryProgress = (
  row: ContactOrderPlanRow,
  communication: CaseCommunicationProjection | null | undefined,
): ContactOrderPlanRow => {
  const progress = providerProgressForCommunication(communication)

  if (!progress) return row

  const executionDefinition = {
    ...row.executionDefinition,
    label: progress.label,
    color: hintColor(progress.color),
    description: progress.detail,
  } as StatusDefinition<ContactStepPhase>
  const hint: ContactOrderPlanHint = {
    label: progress.label,
    subLabel: progress.subLabel,
    color: hintColor(progress.color),
    icon: row.executionDefinition.icon,
    tooltipHtml:
      progress.tooltipHtml || deliveryProgressTooltipHtml(progress.detail, progress.subLabel),
  }

  return {
    ...row,
    executionDefinition,
    executionTooltip: progress.detail,
    hint,
    hintText: progress.label,
    hintTooltip:
      progress.tooltipHtml || [progress.detail, progress.subLabel].filter(Boolean).join("\n"),
  }
}

const isDigitalChannel = (channel?: string | null) => {
  const normalized = String(channel || "").toUpperCase()

  return normalized === "EMAIL" || normalized === "MOBILE_MESSAGE" || normalized === "PHONE"
}

const stepHasVerifiedContactTarget = (
  step: ContactOrderStep,
  contactPointById: Record<string, ContactPoint> = {},
) => Boolean(resolvePlannedContactPoint(step, contactPointById)?.is_verified)

const normalizeId = (value: unknown) => normalizeString(value)

const keyById = <T extends { id?: string | null }>(items: T[] = []) =>
  Object.fromEntries(
    items.map((item) => [item.id, item]).filter(([id]) => normalizeString(id)),
  ) as Record<string, T>

const linkRef = (item: CaseCommunicationTimelineItem, role: string, resource?: string) =>
  normalizeString(
    item.links.find(
      (link) =>
        normalizeCode(link.role) === role &&
        (!resource || normalizeCode(link.ref_resource) === normalizeCode(resource)),
    )?.ref_id,
  )

const caseCommunicationStatus = (item: CaseCommunicationTimelineItem) => {
  const communication = item.communication
  const lifecycle = normalizeCode(communication.lifecycle_status)
  const delivery = normalizeCode(communication.delivery_status)

  if (lifecycle === "SUPPRESSED") return "SUPPRESSED"

  if (lifecycle === "FAILED" || delivery === "FAILED") return "FAILED"

  if (delivery === "ACCEPTED") return "ACCEPTED"

  if (["SENT", "DELIVERED", "READ", "MANUAL_DELIVERED"].includes(delivery)) return "SENT"

  if (
    ["QUEUED", "PREPARED", "MANUAL_PENDING"].includes(delivery) ||
    ["QUEUED", "IN_PROGRESS"].includes(lifecycle)
  ) {
    return "QUEUED"
  }

  return "PLANNED"
}

const caseCommunicationToRowProjection = (
  item: CaseCommunicationTimelineItem,
): CaseCommunicationProjection => {
  const communication = item.communication
  const status = caseCommunicationStatus(item)
  const extension = item.kehrblatt_extension
  const recipient = extension?.recipient
  const content = extension?.content
  const statusDetails = extension?.status_details
  const template = extension?.template
  const templateKey = firstNormalizedString(template?.key, communication.template_key)
  const templateLabel = firstNormalizedString(template?.label, communication.template_label)
  const templateVersion = firstNormalizedString(template?.version, communication.template_version)
  const appointmentScheduleEventId =
    item.appointment_refs?.find((ref) =>
      ["current", "new", "canceled"].includes(normalizeCode(ref.role).toLowerCase()),
    )?.schedule_event_id ?? null

  return {
    id: communication.id,
    organization_id: communication.organization_id,
    caseCommunication: item,
    letter_delivery: item.letter_delivery ?? null,
    work_order_id: linkRef(item, "WORK_ORDER", "work_order") || "",
    schedule_event_id:
      appointmentScheduleEventId || linkRef(item, "SCHEDULE_EVENT", "schedule_event"),
    contact_order_id:
      communication.contact_order_id ?? linkRef(item, "CONTACT_ORDER", "contact_order"),
    contact_step_id: communication.contact_step_id ?? linkRef(item, "CONTACT_STEP", "contact_step"),
    origin_kind: communication.origin_kind,
    purpose: communication.purpose,
    channel: communication.channel,
    direction: communication.direction,
    status,
    lifecycle_status: communication.lifecycle_status,
    delivery_status: communication.delivery_status,
    business_outcome: communication.business_outcome,
    recipient_value: normalizeString(recipient?.target_value),
    recipient_display_name: normalizeString(recipient?.display_name),
    recipient_role: normalizeString(recipient?.role),
    recipient_kind: normalizeString(recipient?.kind),
    recipient_is_verified: recipient?.is_verified ?? null,
    recipient_lifecycle_status: normalizeString(recipient?.lifecycle_status),
    recipient_is_usable: recipient?.is_usable ?? null,
    recipient_source: normalizeString(recipient?.source),
    recipient_confidence: null,
    subject: normalizeString(content?.subject),
    template_key: templateKey,
    template_label: templateLabel,
    template_purpose: communication.purpose,
    template_version: templateVersion,
    provider: communication.provider,
    provider_ref: communication.provider_ref,
    provider_status: communication.provider_status,
    provider_progress: extension?.provider_progress ?? null,
    failure_reason: normalizeString(statusDetails?.failure_reason),
    skipped_reason: normalizeString(statusDetails?.skipped_reason),
    suppression_reason: normalizeString(statusDetails?.suppression_reason),
    appointment_refs: item.appointment_refs,
    queued_at: communication.queued_at,
    sent_at: communication.sent_at ?? communication.delivered_at ?? communication.accepted_at,
    failed_at: communication.failed_at,
    created_at: communication.created_at,
    updated_at: communication.updated_at,
  }
}

const rowProjectionsFromCaseCommunications = (items: CaseCommunicationTimelineItem[] = []) =>
  items.map(caseCommunicationToRowProjection)

const resolveCommunicationStepId = (communication: CaseCommunicationProjection) =>
  normalizeId(communication.contact_step_id)

const representsContactStepExecution = (communication: CaseCommunicationProjection) => {
  const originKind = normalizeCode(communication.origin_kind)

  return (
    originKind === "CONTACT_STEP" ||
    (originKind === "MANUAL" && normalizeCode(communication.purpose) === "LETTER_DELIVERY")
  )
}

const resolveWorkOrderIdForCommunication = ({
  communication,
  order = null,
  scheduleEventById,
  scopedWorkOrderId = null,
}: {
  communication: CaseCommunicationProjection
  order?: ContactOrder | null
  scheduleEventById: Record<string, ScheduleEvent>
  scopedWorkOrderId?: string | null
}) => {
  const orderWorkOrderId = normalizeId(order?.work_order_id)

  if (orderWorkOrderId) return orderWorkOrderId

  const scheduleEventId =
    normalizeId(communication.schedule_event_id) || normalizeId(order?.schedule_event_id)
  const scheduleEventWorkOrderId = scheduleEventId
    ? normalizeId(scheduleEventById[scheduleEventId]?.work_order_id)
    : null

  if (scheduleEventWorkOrderId) return scheduleEventWorkOrderId

  const directWorkOrderId = normalizeId(communication.work_order_id)

  if (directWorkOrderId) return directWorkOrderId

  return normalizeId(scopedWorkOrderId)
}

const communicationFinalityRank = (communication: CaseCommunicationProjection) => {
  const status = normalizeCode(communication.status)

  if (status === "SENT" || status === "DELIVERED") return 5

  if (status === "FAILED") return 4

  if (status === "QUEUED") return 3

  if (status === "PLANNED") return 2

  return 1
}

const shouldUseCommunicationForStep = (
  candidate: CaseCommunicationProjection,
  existing: CaseCommunicationProjection | undefined,
) => {
  if (!existing) return true

  const candidateTime = communicationTimestamp(candidate)
  const existingTime = communicationTimestamp(existing)

  if (candidateTime !== existingTime) return candidateTime > existingTime

  return communicationFinalityRank(candidate) >= communicationFinalityRank(existing)
}

const buildCommunicationRepresentation = ({
  communications = [],
  contactOrderId = null,
}: {
  communications?: CaseCommunicationProjection[]
  contactOrderId?: string | null
}) => {
  const normalizedContactOrderId = normalizeId(contactOrderId)
  const communicationByStepId = new Map<string, CaseCommunicationProjection>()
  const stepIdByCommunicationId = new Map<string, string>()
  const representedStepIds = new Set<string>()

  for (const communication of communications) {
    if (
      normalizedContactOrderId &&
      normalizeId(communication.contact_order_id) !== normalizedContactOrderId
    ) {
      continue
    }

    if (!representsContactStepExecution(communication)) continue

    const stepId = resolveCommunicationStepId(communication)

    if (!stepId) continue

    stepIdByCommunicationId.set(communication.id, stepId)
    representedStepIds.add(stepId)
    const existing = communicationByStepId.get(stepId)

    if (shouldUseCommunicationForStep(communication, existing)) {
      communicationByStepId.set(stepId, communication)
    }
  }

  const representativeCommunicationIds = new Set(
    Array.from(communicationByStepId.values()).map((communication) => communication.id),
  )

  return {
    communicationByStepId,
    stepIdByCommunicationId,
    representedStepIds,
    representativeCommunicationIds,
  }
}

const formatCommunicationRow = ({
  order = null,
  communication,
  primaryRecipientName = null,
  workOrderPropertyAddress = null,
  appointmentTags = [],
  workOrderSummaryTag = null,
  propertyTag = null,
  workOrderId = null,
}: {
  order?: ContactOrder | null
  communication: CaseCommunicationProjection
  primaryRecipientName?: string | null
  workOrderPropertyAddress?: string | null
  appointmentTags?: WorkOrderAppointmentTagPresentation[]
  workOrderSummaryTag?: WorkOrderSummaryTagPresentation | null
  propertyTag?: WorkOrderPropertyContextTagPresentation | null
  workOrderId?: string | null
}): ContactOrderPlanRow => {
  const status = normalizeCode(communication.status) || "UNKNOWN"
  const executionPresentation =
    status === "ACCEPTED"
      ? {
          status: {
            value: "ACCEPTED",
            label: "an Anbieter übergeben",
            icon: mdiMessageTextOutline,
            color: "blue" as StatusDefinition["color"],
          },
          tooltip: "Der Anbieter hat den Versandauftrag angenommen.",
        }
      : communicationExecutionStatus(communication)
  const messagePresentation = communicationMessageType(communication)
  const resolvedTemplateLabel = resolveTemplateLabel({
    label: communication.template_label,
    key: communication.template_key,
    purpose: communication.template_purpose || communication.purpose,
    channel: communication.channel,
  })
  const messageDefinition = messageDefinitionForChannel({
    value: communication.template_key || communication.purpose || messagePresentation.status.value,
    channel: communication.channel,
    source: "communication",
  })
  const recipientCandidate = [
    communication.recipient_role,
    communication.recipient_kind,
    communication.recipient_is_verified,
    communication.recipient_lifecycle_status,
    communication.recipient_is_usable,
    communication.recipient_source,
  ].some((value) => value !== null && value !== undefined)
    ? {
        role: communication.recipient_role,
        kind: communication.recipient_kind,
        is_verified: communication.recipient_is_verified,
        lifecycle_status: communication.recipient_lifecycle_status,
        is_usable: communication.recipient_is_usable,
        source: communication.recipient_source,
      }
    : null
  const recipient = buildRecipientTagPresentation({
    channel: communication.channel,
    candidate: recipientCandidate,
    primaryRecipientName,
    explicitName: normalizeString(communication.recipient_display_name),
    explicitAddress: normalizeString(communication.recipient_value),
    workOrderPropertyAddress,
    isUsable: communication.recipient_is_usable !== false,
  })
  const contact = buildCommunicationContactPresentation({
    channel: communication.channel,
    recipient,
    channelDefinition: messageDefinition,
    purpose: communication.template_purpose || communication.purpose,
    templateKey: communication.template_key,
    templateLabel: resolvedTemplateLabel,
    templateVersion: communication.template_version,
    subject: communication.subject,
    messageDetailsHtml: messagePresentation.tooltip,
    effectiveAt:
      communication.sent_at ||
      communication.failed_at ||
      communication.queued_at ||
      communication.created_at,
  })
  const resultLabel = communicationMessageResult(communication)
  const statusSubLabel = communicationStatusSubLabel(communication)
  const rowWithDeliveryProgress = applyDeliveryProgress(
    {
      id: `communication:${communication.id}`,
      kind: "case_communication",
      source: "case_communication",
      step: null,
      stepActionContext: null,
      caseCommunication: communication.caseCommunication,
      communication: communication.caseCommunication.communication,
      workOrderId: workOrderId || communication.work_order_id || order?.work_order_id || null,
      appointmentTags,
      workOrderSummaryTag,
      propertyTag,
      channel: communication.channel,
      contact,
      targetContactPointUsable: communication.recipient_is_usable !== false,
      dueLabel: formatContactOrderPlanDateTime(
        communication.sent_at ||
          communication.failed_at ||
          communication.queued_at ||
          communication.created_at,
      ),
      status: status as ContactOrderPlanRow["status"],
      statusLabel: executionPresentation.status.label,
      statusTooltip: executionPresentation.tooltip,
      statusTone: status === "FAILED" ? "error" : status === "SENT" ? "success" : "pending",
      statusDefinition: executionPresentation.status,
      statusSubLabel,
      executionDefinition: executionPresentation.status,
      executionTooltip: executionPresentation.tooltip,
      resultDefinition: executionPresentation.status,
      resultText: resultLabel,
      resultTooltip: messagePresentation.tooltip,
      hint: {
        label: resultLabel,
        color: hintColor(executionPresentation.status.color),
        icon: executionPresentation.status.icon,
        tooltipHtml: messagePresentation.tooltip,
      },
      hintText: resultLabel,
      hintTooltip: messagePresentation.tooltip,
      isFuture: false,
      sortTimestamp: communicationTimestamp(communication),
    },
    communication,
  )

  if (normalizeCode(communication.channel) !== "LETTER") return rowWithDeliveryProgress

  const letterPresentation = buildHistoricalLetterDeliveryPresentation({
    delivery: communication.letter_delivery,
    communicationStatus: status,
    baseHint: rowWithDeliveryProgress.hint ?? {
      label: resultLabel,
      color: hintColor(executionPresentation.status.color),
      icon: executionPresentation.status.icon,
      tooltipHtml: messagePresentation.tooltip,
    },
    baseStatusDefinition: rowWithDeliveryProgress.statusDefinition,
    baseStatusSubLabel: rowWithDeliveryProgress.statusSubLabel ?? null,
  })

  return {
    ...rowWithDeliveryProgress,
    contact: buildCommunicationContactPresentation({
      channel: communication.channel,
      recipient: letterPresentation.recipient,
      channelDefinition: messageDefinition,
      purpose: communication.template_purpose || communication.purpose,
      templateKey: communication.template_key,
      templateLabel: resolvedTemplateLabel,
      templateVersion: communication.template_version,
      subject: communication.subject,
      messageDetailsHtml: messagePresentation.tooltip,
      effectiveAt:
        communication.sent_at ||
        communication.failed_at ||
        communication.queued_at ||
        communication.created_at,
    }),
    hint: letterPresentation.hint,
    hintText: letterPresentation.hint.label,
    hintTooltip: letterPresentation.hint.tooltipHtml ?? letterPresentation.hint.subLabel ?? null,
    ...(letterPresentation.status
      ? {
          statusLabel: letterPresentation.status.label,
          statusSubLabel: letterPresentation.status.subLabel,
          statusTooltip: letterPresentation.status.tooltip,
          statusTone: letterPresentation.status.tone,
          statusDefinition: letterPresentation.status.definition,
        }
      : {}),
  }
}

const formatCommunicationRows = (
  order: ContactOrder,
  caseCommunications: CaseCommunicationProjection[] = [],
  primaryRecipientName: string | null = null,
  workOrderPropertyAddress: string | null = null,
  appointmentTags: WorkOrderAppointmentTagPresentation[] = [],
): ContactOrderPlanRow[] =>
  caseCommunications
    .filter((communication) => communication.contact_order_id === order.id)
    .slice()
    .sort((a, b) => communicationTimestamp(a) - communicationTimestamp(b))
    .map((communication) => {
      return formatCommunicationRow({
        order,
        communication,
        primaryRecipientName,
        workOrderPropertyAddress,
        appointmentTags,
      })
    })

export const buildContactOrderPlanRows = ({
  order,
  primaryRecipientName = null,
  workOrderPropertyAddress = null,
  workOrders = [],
  scheduleEvents = [],
  scheduleEventId = null,
  customerById = {},
  contactPointById = {},
  caseCommunications: caseCommunicationItems = [],
  stepPatches = {},
  now = Date.now(),
}: ContactOrderPlanRowsInput): ContactOrderPlanRow[] => {
  const communicationRowsInput = rowProjectionsFromCaseCommunications(caseCommunicationItems)
  const stepActionContextByStepId: Record<string, StepActionContext> = {}
  for (const baseStep of order.steps ?? []) {
    const patch = stepPatches[baseStep.id]
    const step = patch ? ({ ...baseStep, ...patch } as ContactOrderStep) : baseStep
    stepActionContextByStepId[step.id] = { order, step }
  }
  const communicationRepresentation = buildCommunicationRepresentation({
    communications: communicationRowsInput,
    contactOrderId: order.id,
  })
  const {
    communicationByStepId,
    stepIdByCommunicationId,
    representedStepIds,
    representativeCommunicationIds,
  } = communicationRepresentation
  const usedCommunicationIds = new Set<string>()
  const hasVerifiedDigitalStep = (order.steps ?? []).some(
    (step) =>
      isDigitalChannel(step.channel) && stepHasVerifiedContactTarget(step, contactPointById),
  )
  const workOrderById = keyById(workOrders)
  const scheduleEventById = keyById(scheduleEvents)
  const optionalWorkOrderById = workOrderById as Partial<Record<string, WorkOrder>>
  const optionalScheduleEventById = scheduleEventById as Partial<Record<string, ScheduleEvent>>
  const scopedScheduleEventId = normalizeId(scheduleEventId)
  let orderWorkOrder = order.work_order_id
    ? (optionalWorkOrderById[order.work_order_id] ?? null)
    : null

  if (!orderWorkOrder && workOrders.length === 1) {
    orderWorkOrder = workOrders[0] ?? null
  }

  const orderScheduleEventId = normalizeId(order.schedule_event_id) || scopedScheduleEventId
  const orderScheduleEvent = orderScheduleEventId
    ? (optionalScheduleEventById[orderScheduleEventId] ?? null)
    : null
  const orderAppointmentTags = buildCommunicationAppointmentContextTags({
    scheduleEvent: orderScheduleEvent,
    workOrder: orderWorkOrder,
    scheduleEvents,
    customersById: customerById,
    contactPoints: Object.values(contactPointById),
  })
  const planRows = (order.steps ?? [])
    .slice()
    .sort((a, b) => {
      const aDue = a.due_at ? new Date(a.due_at).getTime() : Infinity
      const bDue = b.due_at ? new Date(b.due_at).getTime() : Infinity

      if (aDue !== bDue) return aDue - bDue

      return (a.position ?? 0) - (b.position ?? 0)
    })
    .map((baseStep) => {
      const patch = stepPatches[baseStep.id]
      const step = patch ? ({ ...baseStep, ...patch } as ContactOrderStep) : baseStep
      const replacementCommunication = communicationByStepId.get(step.id)

      if (replacementCommunication) {
        usedCommunicationIds.add(replacementCommunication.id)
      }

      const linkedCommunicationPresentation = replacementCommunication
        ? formatCommunicationRow({
            order,
            communication: replacementCommunication,
            primaryRecipientName,
            workOrderPropertyAddress,
            appointmentTags: orderAppointmentTags,
          })
        : null
      const usesAutomaticLetterServiceStatus =
        normalizeCode(step.channel) === "LETTER" &&
        replacementCommunication?.caseCommunication.letter_delivery?.delivery_mode === "automatic"
      const normalizedStepStatus = String(step.status || "").toUpperCase()
      const dueTimestamp = step.due_at ? new Date(step.due_at).getTime() : NaN
      const statusInfo = resolveContactStepExecutionStatusInfo(step.status)
      const statusDefinition = buildContactStepExecutionStatusDefinition(
        step.status,
      ) as StatusDefinition<ContactStepPhase>
      const effectiveResultStatus =
        step.result_status || (normalizedStepStatus === "CANCELED" ? "CANCELED" : null)
      const targetContactPoint = resolvePlannedContactPoint(step, contactPointById)
      const targetContactPointId = targetContactPoint?.id ?? null
      const recipientCustomer = step.recipient_customer_id
        ? customerById[step.recipient_customer_id]
        : null
      const recipientFacts = {
        customer_id: step.recipient_customer_id,
        customer_name: recipientCustomer?.display_name,
        role: step.recipient_role,
        value: step.destination?.value,
        label: step.destination?.label,
        is_verified: Boolean(targetContactPoint?.is_verified),
      }
      const targetContactPointUsable = isUsableContactPointTarget(
        targetContactPoint,
        recipientFacts,
      )
      const isFuture = Number.isNaN(dueTimestamp) ? true : dueTimestamp > now
      const lastCommunicationSummary = buildLastCommunicationSummary(
        step.id,
        communicationRowsInput,
      )
      const presentation = buildContactStepPresentation({
        stepStatus: step.status,
        resultStatus: effectiveResultStatus,
        resultDescription: step.result_description as string | null,
        resultMeta: step.result_meta as Record<string, unknown> | null,
        resultRecordedAt: step.result_recorded_at as string | null | undefined,
        lastContactLabel: lastCommunicationSummary.label,
        lastContactTooltip: lastCommunicationSummary.tooltip,
        notes: step.notes as string | null,
        channel: step.channel,
        contactPoint: targetContactPoint,
        candidate: recipientFacts,
        targetContactPointUsable,
        executionMode: step.execution_mode,
        hasVerifiedDigitalStep,
        formatDateTime: formatContactOrderPlanDateTime,
      })
      const recipient = buildRecipientTagPresentation({
        channel: step.channel,
        candidate: recipientFacts,
        contactPointId: targetContactPointId,
        customerById,
        contactPointById,
        primaryRecipientName: null,
        workOrderPropertyAddress,
        isUsable: targetContactPointUsable,
      })
      const rawMessageLabel =
        normalizeString(step.template_label) ||
        normalizeString(step.message_label) ||
        normalizeString(step.title)
      const messageValue =
        normalizeString(step.template_key) ||
        normalizeString(step.message_kind) ||
        normalizeString(step.title) ||
        step.id
      const messageDefinition = messageDefinitionForChannel({
        value: messageValue,
        channel: step.channel,
        source: "planned" as const,
      })
      const resolvedTemplateLabel = resolveTemplateLabel({
        label: rawMessageLabel,
        key: messageValue,
        purpose: normalizeString(step.message_kind),
        channel: step.channel,
      })
      const plannedContact = buildCommunicationContactPresentation({
        channel: step.channel,
        recipient,
        channelDefinition: messageDefinition,
        purpose: normalizeString(step.message_kind),
        templateKey: messageValue,
        templateLabel: resolvedTemplateLabel,
        messageDetailsHtml: presentation.hintTooltip,
        effectiveAt: step.result_recorded_at || step.due_at,
      })

      return {
        id: step.id,
        kind: "contact_step" as const,
        source: "planned" as const,
        step,
        stepActionContext: stepActionContextByStepId[step.id],
        caseCommunication: replacementCommunication?.caseCommunication ?? null,
        communication: replacementCommunication?.caseCommunication.communication ?? null,
        workOrderId: order.work_order_id ?? null,
        appointmentTags: orderAppointmentTags,
        channel: step.channel,
        contact: linkedCommunicationPresentation?.contact ?? plannedContact,
        targetContactPointUsable,
        dueLabel: formatContactOrderPlanDateTime(step.due_at as string),
        status: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.status ?? step.status)
          : step.status,
        statusLabel: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.statusLabel ?? statusInfo.label)
          : statusInfo.label,
        statusTooltip: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.statusTooltip ?? statusInfo.description)
          : statusInfo.description,
        statusTone: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.statusTone ?? statusInfo.tone)
          : statusInfo.tone,
        statusDefinition: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.statusDefinition ?? statusDefinition)
          : statusDefinition,
        statusSubLabel: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.statusSubLabel ?? statusSubLabelForStep(step, now))
          : statusSubLabelForStep(step, now),
        executionDefinition: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.executionDefinition ?? statusDefinition)
          : statusDefinition,
        executionTooltip: usesAutomaticLetterServiceStatus
          ? (linkedCommunicationPresentation?.executionTooltip ?? statusInfo.description)
          : statusInfo.description,
        resultDefinition: presentation.resultDefinition,
        resultText: presentation.resultText,
        resultTooltip: presentation.resultTooltip,
        hint: linkedCommunicationPresentation?.hint ?? presentation.hint,
        hintText: linkedCommunicationPresentation?.hintText ?? presentation.hintText,
        hintTooltip: linkedCommunicationPresentation?.hintTooltip ?? presentation.hintTooltip,
        isFuture,
        sortTimestamp: Number.isNaN(dueTimestamp) ? Number.MAX_SAFE_INTEGER : dueTimestamp,
      }
    })

  const communicationRows = formatCommunicationRows(
    order,
    communicationRowsInput.filter((communication) => {
      if (usedCommunicationIds.has(communication.id)) return false

      const stepId = stepIdByCommunicationId.get(communication.id)

      if (!stepId) return true

      if (representativeCommunicationIds.has(communication.id)) return true

      return !representedStepIds.has(stepId)
    }),
    primaryRecipientName,
    workOrderPropertyAddress,
    orderAppointmentTags,
  )
  const sourceRank = {
    planned: 0,
    case_communication: 1,
  } satisfies Record<ContactOrderPlanRow["source"], number>

  return [...planRows, ...communicationRows].sort((left, right) => {
    const leftTimestamp = left.sortTimestamp > 0 ? left.sortTimestamp : Number.MAX_SAFE_INTEGER
    const rightTimestamp = right.sortTimestamp > 0 ? right.sortTimestamp : Number.MAX_SAFE_INTEGER

    if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp

    return sourceRank[left.source] - sourceRank[right.source]
  })
}

const resolveWorkOrderForCommunication = (
  workOrderId: string | null,
  workOrderById: Record<string, WorkOrder>,
) => {
  return workOrderId ? (workOrderById[workOrderId] ?? null) : null
}

const resolveScheduleEventForCommunication = ({
  communication,
  order,
  workOrder,
  scheduleEventById,
}: {
  communication: CaseCommunicationProjection
  order?: ContactOrder | null
  workOrder?: WorkOrder | null
  scheduleEventById: Record<string, ScheduleEvent>
}) => {
  const eventId =
    normalizeId(communication.schedule_event_id) ||
    normalizeId(order?.schedule_event_id) ||
    normalizeId(
      (workOrder as { current_schedule_event_id?: string | null } | null)
        ?.current_schedule_event_id,
    )

  return eventId ? (scheduleEventById[eventId] ?? null) : null
}

const buildCommunicationContextTags = ({
  communication,
  order,
  workOrder,
  propertyById,
  scheduleEventById,
  scheduleEvents,
  customerById,
  contactPointById,
}: {
  communication: CaseCommunicationProjection
  order?: ContactOrder | null
  workOrder?: WorkOrder | null
  propertyById: Record<string, Property>
  scheduleEventById: Record<string, ScheduleEvent>
  scheduleEvents: ScheduleEvent[]
  customerById: Record<string, Customer>
  contactPointById: Record<string, ContactPoint>
}) => {
  const scheduleEvent = resolveScheduleEventForCommunication({
    communication,
    order,
    workOrder,
    scheduleEventById,
  })
  const property = workOrder?.property_id ? (propertyById[workOrder.property_id] ?? null) : null

  return {
    appointmentTags: buildCommunicationAppointmentContextTags({
      appointmentRefs: communication.appointment_refs,
      scheduleEvent,
      workOrder,
      scheduleEvents,
      customersById: customerById,
      contactPoints: Object.values(contactPointById),
    }),
    workOrderSummaryTag: buildWorkOrderSummaryContextTag(workOrder),
    propertyTag: buildWorkOrderPropertyContextTag({ property, workOrder }),
  }
}

export const buildCommunicationPlanRows = ({
  contactOrders = [],
  workOrders = [],
  properties = [],
  scheduleEvents = [],
  customerById = {},
  contactPointById = {},
  caseCommunications: caseCommunicationItems = [],
  stepPatches = {},
  now = Date.now(),
}: CommunicationPlanRowsInput): ContactOrderPlanRow[] => {
  const workOrderById = keyById(workOrders)
  const propertyById = keyById(properties)
  const scheduleEventById = keyById(scheduleEvents)
  const rows = buildWorkOrderPlanningRows({
    contactOrders,
    workOrders,
    properties,
    scheduleEvents,
    customerById,
    contactPointById,
    caseCommunications: caseCommunicationItems,
    stepPatches,
    now,
  }).map((row) => {
    const order = row.stepActionContext?.order ?? null
    const workOrderId = row.workOrderId ?? order?.work_order_id ?? null
    const workOrder = workOrderId ? (workOrderById[workOrderId] ?? null) : null
    const property = workOrder?.property_id ? (propertyById[workOrder.property_id] ?? null) : null
    const scheduleEventId = normalizeId(order?.schedule_event_id)
    const scheduleEvent = scheduleEventId ? (scheduleEventById[scheduleEventId] ?? null) : null

    return {
      ...row,
      workOrderId,
      appointmentTags: row.appointmentTags?.length
        ? row.appointmentTags
        : buildCommunicationAppointmentContextTags({
            scheduleEvent,
            workOrder,
            scheduleEvents,
            customersById: customerById,
            contactPoints: Object.values(contactPointById),
          }),
      workOrderSummaryTag: row.workOrderSummaryTag ?? buildWorkOrderSummaryContextTag(workOrder),
      propertyTag: row.propertyTag ?? buildWorkOrderPropertyContextTag({ property, workOrder }),
    }
  })

  const sourceRank = {
    case_communication: 0,
    planned: 1,
  } satisfies Record<ContactOrderPlanRow["source"], number>

  return rows.sort((left, right) => {
    const leftTimestamp = left.sortTimestamp > 0 ? left.sortTimestamp : Number.MAX_SAFE_INTEGER
    const rightTimestamp = right.sortTimestamp > 0 ? right.sortTimestamp : Number.MAX_SAFE_INTEGER

    if (leftTimestamp !== rightTimestamp) return rightTimestamp - leftTimestamp

    return sourceRank[left.source] - sourceRank[right.source]
  })
}

export const buildWorkOrderPlanningRows = ({
  contactOrders = [],
  workOrders = [],
  properties = [],
  scheduleEvents = [],
  customerById = {},
  contactPointById = {},
  caseCommunications: caseCommunicationItems = [],
  stepPatches = {},
  now = Date.now(),
  workOrderId = null,
  scheduleEventId = null,
  primaryRecipientName = null,
  workOrderPropertyAddress = null,
}: WorkOrderPlanningRowsInput): ContactOrderPlanRow[] => {
  const normalizedWorkOrderId =
    normalizeId(workOrderId) || (workOrders.length === 1 ? normalizeId(workOrders[0]?.id) : null)
  const normalizedScheduleEventId = normalizeId(scheduleEventId)
  const orderById = keyById(contactOrders)
  const scheduleEventById = keyById(scheduleEvents)
  const orderMatchesScope = (order: ContactOrder) => {
    if (normalizedWorkOrderId && normalizeId(order.work_order_id) !== normalizedWorkOrderId)
      return false

    if (
      normalizedScheduleEventId &&
      normalizeId(order.schedule_event_id) !== normalizedScheduleEventId
    )
      return false

    return true
  }

  const communicationMatchesScheduleEvent = (
    communication: CaseCommunicationProjection,
    order: ContactOrder | null,
  ) => {
    if (!normalizedScheduleEventId) return true

    if (normalizeId(communication.schedule_event_id) === normalizedScheduleEventId) return true

    if (normalizeId(order?.schedule_event_id) === normalizedScheduleEventId) return true

    return (communication.appointment_refs ?? []).some(
      (appointmentRef) =>
        normalizeId(appointmentRef.schedule_event_id) === normalizedScheduleEventId,
    )
  }

  const communicationMatchesScope = (communication: CaseCommunicationProjection) => {
    const order = communication.contact_order_id
      ? (orderById[communication.contact_order_id] ?? null)
      : null

    if (normalizedWorkOrderId) {
      const communicationWorkOrderId = resolveWorkOrderIdForCommunication({
        communication,
        order,
        scheduleEventById,
        scopedWorkOrderId: normalizedWorkOrderId,
      })

      if (communicationWorkOrderId !== normalizedWorkOrderId) return false
    }

    return communicationMatchesScheduleEvent(communication, order)
  }

  const scopedOrders = contactOrders.filter(orderMatchesScope)
  const scopedOrderIds = new Set(scopedOrders.map((order) => normalizeId(order.id)).filter(Boolean))
  const scopedCommunicationItems = caseCommunicationItems.filter((item) =>
    communicationMatchesScope(caseCommunicationToRowProjection(item)),
  )
  const planRows = scopedOrders.flatMap((order) =>
    buildContactOrderPlanRows({
      order,
      primaryRecipientName,
      workOrderPropertyAddress,
      workOrders,
      scheduleEvents,
      scheduleEventId: normalizedScheduleEventId,
      customerById,
      contactPointById,
      caseCommunications: scopedCommunicationItems,
      stepPatches,
      now,
    }),
  )

  const workOrderById = keyById(workOrders)
  const propertyById = keyById(properties)
  const standaloneCommunicationById = new Map<string, CaseCommunicationProjection>()
  for (const item of scopedCommunicationItems) {
    const communication = caseCommunicationToRowProjection(item)
    const contactOrderId = normalizeId(communication.contact_order_id)

    if (contactOrderId && scopedOrderIds.has(contactOrderId)) continue

    const existing = standaloneCommunicationById.get(communication.id)

    if (!existing || communicationTimestamp(communication) >= communicationTimestamp(existing)) {
      standaloneCommunicationById.set(communication.id, communication)
    }
  }
  const standaloneRows = Array.from(standaloneCommunicationById.values()).map((communication) => {
    const workOrderIdForCommunication = resolveWorkOrderIdForCommunication({
      communication,
      scheduleEventById,
      scopedWorkOrderId: normalizedWorkOrderId,
    })
    const workOrder = resolveWorkOrderForCommunication(workOrderIdForCommunication, workOrderById)
    const contextTags = buildCommunicationContextTags({
      communication,
      order: null,
      workOrder,
      propertyById,
      scheduleEventById,
      scheduleEvents,
      customerById,
      contactPointById,
    })

    return formatCommunicationRow({
      order: null,
      communication,
      workOrderId: workOrderIdForCommunication,
      primaryRecipientName,
      workOrderPropertyAddress,
      ...contextTags,
    })
  })

  const seenCommunicationIds = new Set<string>()
  const rows = [...planRows, ...standaloneRows].filter((row) => {
    const communicationId = normalizeId(row.caseCommunication?.communication.id)

    if (!communicationId) return true

    if (seenCommunicationIds.has(communicationId)) return false

    seenCommunicationIds.add(communicationId)

    return true
  })
  const sourceRank = {
    planned: 0,
    case_communication: 1,
  } satisfies Record<ContactOrderPlanRow["source"], number>

  return rows.sort((left, right) => {
    const leftTimestamp = left.sortTimestamp > 0 ? left.sortTimestamp : Number.MAX_SAFE_INTEGER
    const rightTimestamp = right.sortTimestamp > 0 ? right.sortTimestamp : Number.MAX_SAFE_INTEGER

    if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp

    const rankDifference = sourceRank[left.source] - sourceRank[right.source]

    return rankDifference || left.id.localeCompare(right.id)
  })
}
